<script setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { picoSearch } from '@scmmishra/pico-search';
import { useStore } from 'dashboard/composables/store';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

import PageLayout from 'dashboard/components-next/pilot/PageLayout.vue';
import SettingsHeader from 'dashboard/components-next/pilot/pageComponents/settings/SettingsHeader.vue';
import SuggestedRules from 'dashboard/components-next/pilot/assistant/SuggestedRules.vue';
import AddNewRulesInput from 'dashboard/components-next/pilot/assistant/AddNewRulesInput.vue';
import AddNewRulesDialog from 'dashboard/components-next/pilot/assistant/AddNewRulesDialog.vue';
import RuleCard from 'dashboard/components-next/pilot/assistant/RuleCard.vue';
import BulkSelectBar from 'dashboard/components-next/pilot/assistant/BulkSelectBar.vue';
import {
  createRule,
  deleteRuleById,
  hasTemplateRule,
  updateRuleContent,
} from 'dashboard/utils/pilot/rules';

const { t } = useI18n();
const route = useRoute();
const store = useStore();
const { uiSettings, updateUISettings } = useUISettings();

const uiFlags = useMapGetter('pilotAssistants/getUIFlags');
const assistantId = computed(() => Number(route.params.assistantId));
const isFetching = computed(() => uiFlags.value.fetchingItem);
const assistant = computed(() =>
  store.getters['pilotAssistants/getRecord'](assistantId.value)
);

const searchQuery = ref('');
const newInlineRule = ref('');
const newDialogRule = ref('');

const guardrailsContent = computed(
  () => assistant.value?.guardrails || []
);

const backUrl = computed(() => ({
  name: 'pilot_assistants_settings_index',
  params: {
    accountId: route.params.accountId,
    assistantId: assistantId.value,
  },
}));

const displayGuardrails = computed(() =>
  guardrailsContent.value
);

const guardrailsExample = [
  {
    id: 'guardrails.pricing.no_quote',
    key: 'guardrails.pricing.no_quote',
    content: '不提供具体的报价信息，引导客户联系销售获取报价',
  },
  {
    id: 'guardrails.competitor.no_negative_comparison',
    key: 'guardrails.competitor.no_negative_comparison',
    content: '不对竞争对手产品做出负面评价或直接对比',
  },
  {
    id: 'guardrails.delivery.no_commitment',
    key: 'guardrails.delivery.no_commitment',
    content: '不承诺交货期，具体交期需与销售确认',
  },
];

const filteredGuardrails = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return displayGuardrails.value;
  return picoSearch(displayGuardrails.value, query, ['content']);
});

const shouldShowSuggestedRules = computed(() => {
  return uiSettings.value?.show_guardrails_suggestions !== false;
});

const closeSuggestedRules = () => {
  updateUISettings({ show_guardrails_suggestions: false });
};

// Bulk selection & hover state
const bulkSelectedIds = ref(new Set());
const hoveredCard = ref(null);

const handleRuleSelect = id => {
  const selected = new Set(bulkSelectedIds.value);
  selected[selected.has(id) ? 'delete' : 'add'](id);
  bulkSelectedIds.value = selected;
};

const handleRuleHover = (isHovered, id) => {
  hoveredCard.value = isHovered ? id : null;
};

const buildSelectedCountLabel = computed(() => {
  const count = displayGuardrails.value.length || 0;
  const isAllSelected = bulkSelectedIds.value.size === count && count > 0;
  return isAllSelected
    ? t('PILOT.ASSISTANTS.GUARDRAILS.BULK_ACTION.UNSELECT_ALL', { count })
    : t('PILOT.ASSISTANTS.GUARDRAILS.BULK_ACTION.SELECT_ALL', { count });
});

const selectedCountLabel = computed(() => {
  return t('PILOT.ASSISTANTS.GUARDRAILS.BULK_ACTION.SELECTED', {
    count: bulkSelectedIds.value.size,
  });
});

const saveGuardrails = async list => {
  await store.dispatch('pilotAssistants/update', {
    id: assistantId.value,
    data: { guardrails: list },
  });
};

const addGuardrail = async (content, templateKey = null) => {
  try {
    const updated = [
      ...guardrailsContent.value,
      createRule({ content: content.trim(), templateKey }),
    ];
    await saveGuardrails(updated);
    useAlert(t('PILOT.ASSISTANTS.GUARDRAILS.API.ADD.SUCCESS'));
  } catch {
    useAlert(t('PILOT.ASSISTANTS.GUARDRAILS.API.ADD.ERROR'));
  }
};

const editGuardrail = async ({ id, content }) => {
  try {
    const updated = updateRuleContent(guardrailsContent.value, id, content);
    await saveGuardrails(updated);
    useAlert(t('PILOT.ASSISTANTS.GUARDRAILS.API.UPDATE.SUCCESS'));
  } catch {
    useAlert(t('PILOT.ASSISTANTS.GUARDRAILS.API.UPDATE.ERROR'));
  }
};

const deleteGuardrail = async id => {
  try {
    const updated = deleteRuleById(guardrailsContent.value, id);
    await saveGuardrails(updated);
    useAlert(t('PILOT.ASSISTANTS.GUARDRAILS.API.DELETE.SUCCESS'));
  } catch {
    useAlert(t('PILOT.ASSISTANTS.GUARDRAILS.API.DELETE.ERROR'));
  }
};

const bulkDeleteGuardrails = async () => {
  if (bulkSelectedIds.value.size === 0) return;
  try {
    const updated = guardrailsContent.value.filter(
      rule => !bulkSelectedIds.value.has(rule.id)
    );
    await saveGuardrails(updated);
    bulkSelectedIds.value.clear();
    useAlert(t('PILOT.ASSISTANTS.GUARDRAILS.API.DELETE.SUCCESS'));
  } catch {
    useAlert(t('PILOT.ASSISTANTS.GUARDRAILS.API.DELETE.ERROR'));
  }
};

const addAllExample = async () => {
  updateUISettings({ show_guardrails_suggestions: false });
  try {
    const newGuardrails = [
      ...guardrailsContent.value,
      ...guardrailsExample
        .filter(example => !hasTemplateRule(guardrailsContent.value, example.key))
        .map(example =>
          createRule({ content: example.content, templateKey: example.key })
        ),
    ];
    await saveGuardrails(newGuardrails);
    useAlert(t('PILOT.ASSISTANTS.GUARDRAILS.API.ADD.SUCCESS'));
  } catch {
    useAlert(t('PILOT.ASSISTANTS.GUARDRAILS.API.ADD.ERROR'));
  }
};
</script>

<template>
  <PageLayout
    :header-title="$t('PILOT.ASSISTANTS.GUARDRAILS.TITLE')"
    :is-fetching="isFetching"
    :back-url="backUrl"
    :show-know-more="false"
    :show-pagination-footer="false"
    :show-assistant-switcher="false"
  >
    <template #body>
      <SettingsHeader
        :heading="$t('PILOT.ASSISTANTS.GUARDRAILS.TITLE')"
        :description="$t('PILOT.ASSISTANTS.GUARDRAILS.DESCRIPTION')"
      />
      <div v-if="shouldShowSuggestedRules" class="flex mt-7 flex-col gap-4">
        <SuggestedRules
          :title="$t('PILOT.ASSISTANTS.GUARDRAILS.ADD.SUGGESTED.TITLE')"
          :items="guardrailsExample"
          @add="addAllExample"
          @close="closeSuggestedRules"
        >
          <template #default="{ item }">
            <div class="flex items-center justify-between w-full">
              <span class="text-sm text-n-slate-12">
                {{ item.content }}
              </span>
              <Button
                :label="
                  hasTemplateRule(guardrailsContent, item.key)
                    ? '已添加'
                    : $t('PILOT.ASSISTANTS.GUARDRAILS.ADD.SUGGESTED.ADD_SINGLE')
                "
                ghost
                xs
                slate
                class="!text-sm !text-n-slate-11 flex-shrink-0"
                :disabled="hasTemplateRule(guardrailsContent, item.key)"
                @click="addGuardrail(item.content, item.key)"
              />
            </div>
          </template>
        </SuggestedRules>
      </div>
      <div class="flex mt-7 flex-col gap-4">
        <div class="flex justify-between items-center">
          <BulkSelectBar
            v-model="bulkSelectedIds"
            :all-items="displayGuardrails"
            :select-all-label="buildSelectedCountLabel"
            :selected-count-label="selectedCountLabel"
            :delete-label="
              $t('PILOT.ASSISTANTS.GUARDRAILS.BULK_ACTION.BULK_DELETE_BUTTON')
            "
            @bulk-delete="bulkDeleteGuardrails"
          >
            <template #default-actions>
              <AddNewRulesDialog
                v-model="newDialogRule"
                :placeholder="
                  t('PILOT.ASSISTANTS.GUARDRAILS.ADD.NEW.PLACEHOLDER')
                "
                :button-label="t('PILOT.ASSISTANTS.GUARDRAILS.ADD.NEW.TITLE')"
                :confirm-label="t('PILOT.ASSISTANTS.GUARDRAILS.ADD.NEW.CREATE')"
                :cancel-label="t('PILOT.ASSISTANTS.GUARDRAILS.ADD.NEW.CANCEL')"
                @add="addGuardrail"
              />
            </template>
          </BulkSelectBar>
          <div
            v-if="displayGuardrails.length && bulkSelectedIds.size === 0"
            class="max-w-[22.5rem] w-full min-w-0"
          >
            <Input
              v-model="searchQuery"
              :placeholder="
                t('PILOT.ASSISTANTS.GUARDRAILS.LIST.SEARCH_PLACEHOLDER')
              "
            />
          </div>
        </div>
        <div v-if="displayGuardrails.length === 0" class="mt-1 mb-2">
          <span class="text-n-slate-11 text-sm">
            {{ t('PILOT.ASSISTANTS.GUARDRAILS.EMPTY_MESSAGE') }}
          </span>
        </div>
        <div v-else-if="filteredGuardrails.length === 0" class="mt-1 mb-2">
          <span class="text-n-slate-11 text-sm">
            {{ t('PILOT.ASSISTANTS.GUARDRAILS.SEARCH_EMPTY_MESSAGE') }}
          </span>
        </div>
        <div v-else class="flex flex-col gap-2">
          <RuleCard
            v-for="guardrail in filteredGuardrails"
            :id="guardrail.id"
            :key="guardrail.id"
            :content="guardrail.content"
            :is-selected="bulkSelectedIds.has(guardrail.id)"
            :selectable="
              hoveredCard === guardrail.id || bulkSelectedIds.size > 0
            "
            @select="handleRuleSelect"
            @edit="editGuardrail"
            @delete="deleteGuardrail"
            @hover="isHovered => handleRuleHover(isHovered, guardrail.id)"
          />
        </div>
        <AddNewRulesInput
          v-model="newInlineRule"
          :placeholder="
            t('PILOT.ASSISTANTS.GUARDRAILS.ADD.SUGGESTED.PLACEHOLDER')
          "
          :label="t('PILOT.ASSISTANTS.GUARDRAILS.ADD.SUGGESTED.SAVE')"
          @add="addGuardrail"
        />
      </div>
    </template>
  </PageLayout>
</template>
